import { useEffect, useState } from "react";
import { AlignItems, Box, Color } from "@gocardless/flux-react";
import { parse } from "query-string";
import { HTTPError } from "@gocardless/api/utils/api";
import _ from "lodash";

import { SignUpAccountType } from "../SignUp";
import { ExpiredReferralCode } from "../form-field-components/ExpiredReferralCode";

import { EmailVerification } from "./EmailVerification/EmailVerification";
import { useSignup } from "./useSignup";
import { SignupMarketing } from "./SignupMarketing";
import { LoginCTA } from "./signup-form/LoginCTA";
import { SignupForm } from "./signup-form/SignupForm";
import {
  getReferralEmailVariation,
  getReferralSource,
  useHttpErrorHandler,
} from "./helper";
import { useResendCode } from "./useResendCode";

import { Loader } from "src/components/ui/Loader/Loader";
import LoaderOutlineGC from "src/assets/svg/loader-outline-gc.svg";
import { CountryCodeSupported } from "src/common/country";
import { useRouterQuery } from "src/common/routing";
import {
  OB_SEARCH_QUERY_KEY,
  SignupFormData,
} from "src/components/routes/SignUp/optimised-signup/consts";
import { setItem } from "src/common/local-storage/local-storage";
import { VerticalSplitPageLayout } from "src/components/layout/VerticalSplitPage/VerticalSplitPageLayout";

interface SignUpProps {
  defaultCountryCode: CountryCodeSupported;
  accountType: SignUpAccountType;
  isPaymentProvider?: boolean;
  hasFsbMemberDiscount?: boolean;
}

export const OptimisedSignup: React.FC<SignUpProps> = ({
  defaultCountryCode,
  accountType,
  hasFsbMemberDiscount,
}) => {
  const { item: referralGeo } = useRouterQuery("referral_geo");
  const { item: referralCode } = useRouterQuery("referral_code");
  const { item: referralCodeName } = useRouterQuery("referral_code_name");
  const { item: defaultReferralGeo } = useRouterQuery("geo");
  const { item: defaultReferralCodeName } = useRouterQuery("name");
  const { item: defaultReferralCode } = useRouterQuery("code");
  const { item: defaultReferralSource } = useRouterQuery("s");
  const { item: defaultReferralEmailVariation } = useRouterQuery("v");

  const referralsSource = getReferralSource(
    defaultReferralSource,
    defaultReferralEmailVariation
  );
  const referralEmailVariation = getReferralEmailVariation(
    defaultReferralEmailVariation
  );

  const [showSignupForm, setShowSignupForm] = useState(true);
  const { handleError } = useHttpErrorHandler();
  const { notifyEmailSent } = useResendCode();

  const {
    signupFormData,
    createEmailVerification,
    submitEmailVerificationRegistration,
    isSubmittingRegistration,
    isSubmittingEmailVerification,
  } = useSignup({
    onEmailVerificationSuccess: () => {
      setShowSignupForm(false);
    },
    onEmailVerificationError: (error: HTTPError) => {
      handleError(error);
    },
    userCountryCode: defaultCountryCode,
    accountType,
    setShowSignupForm,
  });

  const qs = parse(window.location.search);
  if (Object.keys(qs).includes("ob")) {
    setItem(OB_SEARCH_QUERY_KEY, "true");
  }

  const resendCode = (formData: SignupFormData) => {
    createEmailVerification(formData);
    notifyEmailSent(formData.email);
  };

  useEffect(() => {
    if (!showSignupForm) {
      window.scrollTo(0, 0);
    }
  }, [showSignupForm]);
  return isSubmittingRegistration ? (
    <Loader imgSrc={LoaderOutlineGC} text="" />
  ) : (
    <VerticalSplitPageLayout
      logoVariant="dark"
      logoURL="https://gocardless.com"
      leftContent={<SignupMarketing />}
      leftBackground={{ backgroundColor: Color.Ultraviolet_50 }}
      topRightButton={<LoginCTA />}
      rightContent={
        <Box width="100%" height="100%">
          {showSignupForm ? (
            <Box layout="flex" alignItems={AlignItems.Center} height="100%">
              {/* Temp to invalidate usage of V2 referral codes.
              V3 codes are all 6 characters in length. */}
              {referralCode && referralCode.length > 6 ? (
                <ExpiredReferralCode />
              ) : (
                <SignupForm
                  defaultCountryCode={defaultCountryCode}
                  onSubmit={createEmailVerification}
                  defaultFormData={signupFormData}
                  isSubmittingEmailVerification={isSubmittingEmailVerification}
                  isSubmittingRegistration={isSubmittingRegistration}
                  referralGeo={
                    defaultReferralGeo
                      ? _.toUpper(defaultReferralGeo)
                      : referralGeo
                  }
                  referralCode={defaultReferralCode || referralCode}
                  referralSource={referralsSource}
                  referralVariation={referralEmailVariation}
                  referralCodeName={
                    defaultReferralCodeName
                      ? _.capitalize(defaultReferralCodeName)
                      : referralCodeName
                  }
                  hasFsbMemberDiscount={!!hasFsbMemberDiscount}
                />
              )}
            </Box>
          ) : (
            <EmailVerification
              formData={signupFormData}
              setShowSignupForm={setShowSignupForm}
              onSubmit={submitEmailVerificationRegistration}
              isSubmittingRegistration={isSubmittingRegistration}
              resendCode={resendCode}
            />
          )}
        </Box>
      }
    />
  );
};
